'use client';

import { useState } from 'react';

import { unstable_isUnrecognizedActionError } from 'next/dist/client/components/unrecognized-action-error';

import { registerCustomer } from '@/actions';
import { type CheckoutCheckboxesConfig } from '@/brands/types';
import { formatDocument, formatPhone } from '@/components/ui';
import { useRegistrationCheckboxes } from '@/hooks/useRegistrationCheckboxes';
import { toast, useForm, zodResolver, type Control, type FieldErrors } from '@/libs';
import { useCheckoutStore, useCustomerStore } from '@/stores';
import { formatErrorMessage } from '@/types/models/ApiError';
import {
  registrationFormSchema,
  type RegistrationFormData,
} from '@/types/models/CustomerRegistrationForm';
import { logger } from '@/utils';
import { clearCachedCustomerProfile } from '@/utils/customerProfileCache';
import { addDDIToPhone, removeDDIFromAutoInsert } from '@/utils/formatters';
import {
  EMAIL_ALREADY_REGISTERED_MESSAGE,
  isEmailAlreadyRegisteredError,
} from '@/utils/registrationErrors';

import { CUSTOMER_GUEST_MESSAGES } from '../constants';

export type RegistrationSeed = {
  document: string;
  phone: string;
  customerId: string;
  name: string;
  hasMail: boolean;
  hasAddress: boolean;
};

export type RegisteredUserData = {
  name: string;
  socialName?: string;
  document: string;
  phone: string;
  customerId: string;
  hasAccount: boolean;
  hasMail: boolean;
  hasAddress: boolean;
};

type UseUserRegisterFormProps = {
  seed: RegistrationSeed;
  onRegisterSuccess: (data: RegisteredUserData) => void;
};

type UseUserRegisterFormReturn = {
  control: Control<RegistrationFormData>;
  errors: FieldErrors<RegistrationFormData>;
  isLoading: boolean;
  isEmailRequired: boolean;
  isDirty: boolean;
  showSocialName: boolean;
  setShowSocialName: (value: boolean) => void;
  checkboxes: CheckoutCheckboxesConfig;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

export const useUserRegisterForm = ({
  seed,
  onRegisterSuccess,
}: UseUserRegisterFormProps): UseUserRegisterFormReturn => {
  const utmData = useCheckoutStore((state) => state.checkoutData.utmData);
  const setCustomerData = useCustomerStore((state) => state.setCustomerData);
  const checkboxes = useRegistrationCheckboxes();

  const [showSocialName, setShowSocialName] = useState(false);

  const isEmailRequired = !seed.hasMail;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<RegistrationFormData>({
    mode: 'onChange',
    resolver: zodResolver(registrationFormSchema),
    defaultValues: {
      document: formatDocument(seed.document),
      fullname: seed.name,
      socialName: '',
      hasSocialName: false,
      email: undefined,
      phone: formatPhone(removeDDIFromAutoInsert(seed.phone)),
      phoneConfirmation: '',
      MarketingOptIn: false,
      isTermsOfServiceAccepted: false,
    },
  });

  const onSubmit = async (data: RegistrationFormData) => {
    const normalizedEmail = (data.email || '').trim();

    if (isEmailRequired && !normalizedEmail) {
      setError('email', { type: 'manual', message: 'E-mail é obrigatório' });
      return;
    }

    if (checkboxes.termsOfService.visible && checkboxes.termsOfService.required) {
      if (!data.isTermsOfServiceAccepted) {
        setError('isTermsOfServiceAccepted', {
          type: 'manual',
          message: checkboxes.termsOfService.errorMessage,
        });
        return;
      }
    }

    if (checkboxes.marketingOptIn.visible && checkboxes.marketingOptIn.required) {
      if (!data.MarketingOptIn) {
        setError('MarketingOptIn', {
          type: 'manual',
          message: checkboxes.marketingOptIn.errorMessage,
        });
        return;
      }
    }

    const phoneWithCountryCode = addDDIToPhone(data.phone);
    const resolvedName = data.hasSocialName
      ? data.socialName || data.fullname
      : data.fullname;

    try {
      const { data: registeredCustomer, error } = await registerCustomer({
        name: resolvedName,
        socialName: data.hasSocialName ? data.socialName : undefined,
        cpf: data.document,
        email: normalizedEmail || undefined,
        phone: phoneWithCountryCode,
        marketingOptIn: data.MarketingOptIn,
        utm: utmData || undefined,
      });

      if (error || !registeredCustomer) {
        throw new Error(error?.friendlyMessage || 'Erro ao criar cadastro');
      }

      clearCachedCustomerProfile(registeredCustomer.customerId);

      const registered: RegisteredUserData = {
        name: resolvedName,
        socialName: data.hasSocialName ? data.socialName : undefined,
        document: data.document,
        phone: phoneWithCountryCode,
        customerId: registeredCustomer.customerId,
        hasAccount: true,
        hasMail: seed.hasMail || Boolean(normalizedEmail),
        hasAddress: seed.hasAddress,
      };

      setCustomerData({
        name: registered.name,
        ...(registered.socialName ? { socialName: registered.socialName } : {}),
        document: registered.document,
        email: normalizedEmail,
        phone: registered.phone,
        customerId: registered.customerId,
        hasAccount: true,
        hasMail: registered.hasMail,
        hasAddress: registered.hasAddress,
      });

      toast.success(CUSTOMER_GUEST_MESSAGES.ACCOUNT_CREATED);
      onRegisterSuccess(registered);
    } catch (error) {
      logger.error('Error registering customer from drawer:', error);

      if (unstable_isUnrecognizedActionError(error)) {
        toast.warning('Nova versão disponível', {
          description: 'Recarregue a página para continuar.',
          action: { label: 'Recarregar', onClick: () => location.reload() },
        });
        return;
      }

      if (isEmailAlreadyRegisteredError(error)) {
        setError('email', { type: 'manual', message: EMAIL_ALREADY_REGISTERED_MESSAGE });
        toast.error(EMAIL_ALREADY_REGISTERED_MESSAGE);
        return;
      }

      toast.error(formatErrorMessage(error, CUSTOMER_GUEST_MESSAGES.DEFAULT_ERROR));
    }
  };

  return {
    control,
    errors,
    isLoading: isSubmitting,
    isEmailRequired,
    isDirty,
    showSocialName,
    setShowSocialName,
    checkboxes,
    handleSubmit: handleSubmit(onSubmit),
  };
};
